import React from "react";
//Limitless imports
import { processMessageResourceCache, CLAIMED_FOR_RESPONSE_DURING_REVIEW, CLAIM_TIMEOUT, CLAIMED_BY_ANOTHER, CLOSED, UNAVAILABLE, isExpertMessageEditorState, validateTemplateContent, isStateLocked } from "../../../portal";
import { useNotificationContext } from "@limitless/common/hooks/notification/NotificationContext";
import { useAppConfig } from "@limitless/common/hooks";
import { useExpertMessageContext } from "../expert-message/ExpertMessageContext";
import { useExpertTaskListContext } from "../expert-task-list/ExpertTaskListContext";
import { subscribeToGroups, unSubscribeGroups, getWebSocketSubscriptionStatus, subscribeToSupportChannel, unsubscribeFromSupportChannel } from "../../utils/WebSocketHelper";
import { useExpertPendingFeeAllocationContext } from "../expert-pending-fee-allocation/ExpertPendingFeeAllocationContext";
import { useExpertAnnouncementsContext } from "../expert-announcements/ExpertAnnouncementsContext";
import { log as pilog, WSClient } from "@limitless/common/utils";
import { useExpertProfileContext } from "../expert-profile/ExpertProfileContext";
import { useCommonPortalWebSocketHook } from "../common-web-sockets/CommonPortalWebSocketHook";
import { useExpertSupportConversationHistoryContext } from "../expert-support-conversation-history/ExpertSupportConversationHistoryContext";
import { useExpertWebNotificationHook } from "../expert-web-notification/ExpertWebNotificationHook";
import { TASKLIST_MINIMUM_THRESHOLD } from "../../../portal/utils/Constants";
const win = typeof window === "undefined" ? {} : window;
win.Paho = {
  MQTT: WSClient
};

// WebSocket actions
export const NEW_WS_ACTION = "new";
export const ACTIVITY_CONTROL_CHANGE = "activityControlChange";
export const READ_WS_ACTION = "read";
export const DELETED_WS_ACTION = "deleted";
export const CLAIMED_WS_ACTION = "claim";
export const CLOSED_WS_ACTION = "close";
export const EXPIRED_WS_ACTION = "expired";
export const REPORT_WS_ACTION = "report";
export const FORWARD_WS_ACTION = "forward";
export const QUARANTINE_WS_ACTION = "quarantine";
export const UNREPORT_WS_ACTION = "unreport";
export const UNCLAIMED_WS_ACTION = "unclaim";
const CLAIM_EXPIRE_WS_ACTION = "claimExpire";
const CUSTOMER_CONFIRMATION_STATE_WS_ACTION = "custConfirmationState";
const MESSAGE_FEE_ALLOCATION_WS_ACTION = "pendingMessageFee";
const EXPIRY_TIME_WS_ACTION = "expiry";
const STICKY_TIME_WS_ACTION = "stickyExpire";
const STICKY_ASSIGNED_ACTION = "stickyAssigned";
const PROFILE_UPDATE_WS_ACTION = "profileUpdate";
const NEW_SECURE_DATA = "newSecureData";
const PASSIVE_AI_REIVEW_COMPLETE = "passiveAIReviewCompleted";
const PASSIVE_AI_RESPOND_COMPLETE = "passiveAIRespondCompleted";
const PASSIVE_AI_RESPOND_FAILED = "passiveAIRespondFailed";

// WebSocket types
const ANNOUNCEMENT = "announcement";
const MESSAGE = "message";
const DIALOGUE = "dialogue";
const RESOURCE_CACHE = "resource-cache";
const USER = "user";
const RELEASE = "release";
export const useExpertWebSockets = () => {
  const log = pilog.getLogger("useExpertWebSockets");
  const [routeName, setRouteName] = React.useState("");
  const [localClient, setLocalClient] = React.useState(false);
  const {
    onConnect: commonConnect,
    onDisconnect: commonDisconnect,
    processWebSocketEvent: processCommonEvent,
    logEvent
  } = useCommonPortalWebSocketHook();
  const {
    getTasksStickyThreshold,
    getDashboardMsgCount
  } = useAppConfig();
  const [passiveQrEventFlag, setPassiveQrEventFlag] = React.useState(false);
  const {
    sendBrowserNotification
  } = useExpertWebNotificationHook();
  const messageContext = useExpertMessageContext();
  const tasklistContext = useExpertTaskListContext();
  const {
    updateMessagePendingFeeAllocation
  } = useExpertPendingFeeAllocationContext();
  const {
    addAnnouncement
  } = useExpertAnnouncementsContext();
  const {
    addInfoNotification
  } = useNotificationContext();
  const {
    profile,
    updateProfile
  } = useExpertProfileContext();
  const {
    history,
    initialHistory,
    updateHistoryMessage
  } = useExpertSupportConversationHistoryContext();
  const {
    message,
    addNewDialogue,
    changeMessageState,
    setMessage,
    clearMessage,
    getCustomerSecureData
  } = messageContext;
  const {
    addTaskListRecord,
    tasklist,
    taskCount,
    removeTasklistRecord,
    updateTaskListRecord,
    addTasklistCallback
  } = tasklistContext;
  const [passiveRespondEventData, setPassiveRespondEventData] = React.useState(null);
  const [passiveRespondEvent, setPassiveRespondEvent] = React.useState({
    flag: false,
    failed: false,
    messageID: null,
    animation: true,
    buttonEnabled: false
  });
  const getStickyTaskListThreshold = getTasksStickyThreshold();
  const getDashboardTaskCount = getDashboardMsgCount();
  log.debug("useExpertWebSockets called ", {
    tasklistContext,
    routeName
  });

  // Exper support web sockets subscriptions
  React.useEffect(() => {
    if ((history === null || history === void 0 ? void 0 : history.activeMessages.length) > 0 && localClient && initialHistory) {
      history.activeMessages.forEach(message => {
        subscribeToSupportChannel(localClient, message.messageId);
      });
    }
  }, [initialHistory, localClient]);
  const onHookInitialisation = ({
    conditionalWSReconnect
  }) => {
    //register a tasklist callback
    //every time the tasklist is fetched, check to ensure we're connected to web sockets
    addTasklistCallback(conditionalWSReconnect);
  };
  const processMessageEvent = event => {
    if (!event.payload) {
      log.error("Event payload not found", event);
      return;
    }
    switch (event.action) {
      case READ_WS_ACTION:
        // this is to update task badge count.
        if (profile.userName === event.payload.userId) {
          updateTaskListRecord({
            messageId: event.payload.messageId,
            new: false
          });
        }
        break;
      case STICKY_TIME_WS_ACTION:
        if (tasklist.messages[event.payload.messageId]) {
          updateTaskListRecord({
            messageId: event.payload.messageId,
            assignedExpertExpiry: null
            // type: event.payload.inbox?.type ?? "respond"
          });
        }
        break;
      case STICKY_ASSIGNED_ACTION:
        updateTaskListRecord({
          messageId: event.payload.messageId,
          ...event.payload.inbox
        });
        break;
      case EXPIRY_TIME_WS_ACTION:
        updateTaskListRecord(event.payload);
        break;
      case UNREPORT_WS_ACTION:
        if (!event.payload.passed) {
          addMessageToTaskList(event);
        }
        break;
      case NEW_WS_ACTION:
        if (process.env.REACT_APP_TYPE !== "mobile") {
          sendBrowserNotification();
        }

        //this will replace an existing message
        if (message.messageId === event.payload.messageId) {
          addMessageToTaskList({
            ...event,
            payload: {
              ...event.payload,
              new: false
            }
          });
          const newMessage = {
            ...message,
            inbox: {
              ...message.inbox,
              ...event.payload
            }
          };
          changeMessageState(null, newMessage);
        } else {
          //only add to the inbox if it's not passed
          // if (!event.payload.passed) {
          // As a customer added a new dialogue to the sticky message & the message is not exist in the task list
          if (!tasklist.messages[event.payload.messageId] && event.payload.type === "respondSticky") {
            const stickyNotification = event.payload.expertDialogueExists ? "A customer has replied to you" : "You have been assigned a new task";
            addInfoNotification(stickyNotification);
          }
          const userIdSet = new Set(Object.values(tasklist.filteredMessages).map(item => item.userId));
          const maxExpiry = Math.max(...Object.values(tasklist.filteredMessages).map(msg => msg.expires), 0 // Default value if no messages exist
          );
          const totalTasks = Object.values(tasklist.filteredMessages).map(item => item).length;
          const isStickyMessage = event.payload.type === "respondSticky";
          const isStickyThresholdExceeded = tasklist.stickyMessageCount >= getStickyTaskListThreshold;
          if (new Date(event.timestamp).getTime() > (tasklist === null || tasklist === void 0 ? void 0 : tasklist.lastTaskListFetch)) {
            // Sticky IOT Logic
            if (isStickyMessage && !isStickyThresholdExceeded) {
              addMessageToTaskList(event);
            } else if (tasklist.stickyMessageCount < getStickyTaskListThreshold) {
              if (event.payload.expires < maxExpiry) {
                // IOT Logic
                addMessageToTaskList(event);
              }
              if (totalTasks < getDashboardTaskCount) {
                addMessageToTaskList(event);
              }
            } else if (isStickyThresholdExceeded && isStickyMessage) {
              addMessageToTaskList(event, true);
              const filteredStickyMessages = Object.fromEntries(Object.entries(tasklist.filteredMessages).filter(([_, msg]) => msg.type === "respondSticky"));
              // Update tasklist with the filtered messages
              tasklist.filteredMessages = filteredStickyMessages;
            }
          }
        }
        break;
      case CLAIMED_WS_ACTION:
        if (profile.userName !== event.payload.userId) {
          updateTaskListRecord({
            messageId: event.payload.messageId,
            claimedByAnother: true
          });
          changeViewingMessageState(event, CLAIMED_BY_ANOTHER);
        }
        break;
      case CLAIM_EXPIRE_WS_ACTION:
        if (message.messageId === event.payload.messageId) {
          if (message.conversationState && isExpertMessageEditorState(message.conversationState.state)) {
            changeViewingMessageState(event, CLAIM_TIMEOUT);
          }
          if (event.payload.reported) {
            removeTasklistRecord(event.payload.messageId);
          } else {
            updateTaskListRecord({
              ...event.payload,
              claimedByAnother: false
            });
          }
        }
        break;
      case PASSIVE_AI_REIVEW_COMPLETE:
        setPassiveQrEventFlag(true);
        break;
      case PASSIVE_AI_RESPOND_COMPLETE:
        setPassiveRespondEvent(prev => ({
          ...prev,
          flag: true,
          failed: false,
          animation: false,
          buttonEnabled: true,
          messageId: event.payload.messageId
        }));
        setPassiveRespondEventData({
          payload: event.payload
        });
        break;
      case PASSIVE_AI_RESPOND_FAILED:
        setPassiveRespondEvent(prev => ({
          ...prev,
          failed: true,
          animation: false,
          buttonEnabled: false,
          flag: false
        }));
        setPassiveRespondEventData({
          payload: null
        });
        break;
      case CUSTOMER_CONFIRMATION_STATE_WS_ACTION:
        if (message.messageId === event.payload.messageId) {
          setMessage({
            ...message,
            custConfirmationState: event.payload.custConfirmationState,
            hasFlaggedConversationComplete: false
          });
        }
        break;
      case UNCLAIMED_WS_ACTION:
      case QUARANTINE_WS_ACTION:
        removeMessageAndUpdateState(event, UNAVAILABLE);
        break;

      //this was introduced to handle the platform sending a Claim IoT then immediately a Delete Iot, messing up the Claimed By Another animation
      case DELETED_WS_ACTION:
        if (message.messageId === event.payload.messageId) {
          updateTaskListRecord({
            messageId: event.payload.messageId,
            claimedByAnother: true
          });
          setTimeout(() => {
            removeMessageAndUpdateState(event, CLOSED);
          }, 5000); // 5 seconds delay
        } else if (tasklist.messages[event.payload.messageId]) {
          if (!tasklist.messages[event.payload.messageId].claimedByAnother) {
            removeMessageAndUpdateState(event, CLOSED);
            // this was introduced to handle the platform sending deleted IOT first following claim IOT for Claimed by another animation
            updateTaskListRecord({
              messageId: event.payload.messageId,
              claimedByAnother: true
            });
          }
        }
        break;
      case EXPIRED_WS_ACTION:
      case REPORT_WS_ACTION:
      case FORWARD_WS_ACTION:
      case CLOSED_WS_ACTION:
        removeMessageAndUpdateState(event, CLOSED);
        break;
      case NEW_SECURE_DATA:
        processDialogueEvent(event);
        getCustomerSecureData();
        break;
      default:
        {
          log.info(`Unsupported WebSocket message event action: ${event.action}`);
        }
    }
  };
  const addMessageToTaskList = (event, isStickyThresholdExceeded = false) => {
    event.payload.claimedByAnother = false;
    event.payload.groupName = event.payload.displayName || event.payload.groupName;
    addTaskListRecord(event.payload, isStickyThresholdExceeded);
  };
  const removeMessageAndUpdateState = (event, state) => {
    //only manipulate state if there isn't an API call in progress
    if (!tasklist.loading) {
      removeTasklistRecord(event.payload.messageId);
      if (message.messageId === event.payload.messageId) {
        var _message$inbox;
        if ((_message$inbox = message.inbox) !== null && _message$inbox !== void 0 && _message$inbox.claimed) {
          changeViewingMessageState(event, state);
        } else {
          clearMessage();
        }
      }
    }
    if (state === CLOSED) {
      try {
        log.warn(`${event.action} Web Socket event received %o %o`, event, message);
      } catch {
        log.warn(`${CLOSED} Web Socket event received`);
      }
    }
  };
  const changeViewingMessageState = (event, state) => {
    if (message.messageId === event.payload.messageId) {
      const newMessage = {
        ...message,
        ...event.payload
      };
      changeMessageState(state, newMessage);
    }
  };
  const processDialogueEvent = event => {
    var _event$payload;
    if ((_event$payload = event.payload) !== null && _event$payload !== void 0 && _event$payload.messageId) {
      if (event.payload.messageId === message.messageId) {
        const messageProps = {};
        //eslint-disable-next-line
        if (event.payload.custConfirmationState !== undefined) {
          messageProps.custConfirmationState = event.payload.custConfirmationState;
          messageProps.hasFlaggedConversationComplete = false;
        }
        //reset the hasAddedDialogue as we have a new customer dialogue
        if (event.payload.origin === "customer") {
          messageProps.hasAddedDialogue = false;
          messageProps.hasFlaggedConversationComplete = false;
          messageProps.isLastCustomerDialogue = true;
          setPassiveRespondEvent({
            flag: false,
            failed: false,
            animation: true,
            buttonEnabled: false,
            messageId: event.payload.messageId
          });
          setPassiveRespondEventData({
            payload: null
          });
        }
        let stateOverride = null;
        //for the scenario where we are replying to the message during a review
        //adding a new dialogue triggers state recalculation
        //we don't want to flip back to review
        if (message.conversationState.state === CLAIMED_FOR_RESPONSE_DURING_REVIEW) {
          stateOverride = CLAIMED_FOR_RESPONSE_DURING_REVIEW;
        }
        addNewDialogue(event.payload, messageProps, stateOverride);
        if (process.env.REACT_APP_TYPE === "mobile" && routeName === "Classification") {
          addInfoNotification("A customer has replied to you");
        }
      } else if (tasklist.messages[event.payload.messageId] && !tasklist.messages[event.payload.messageId].new && tasklist.messages[event.payload.messageId].type === "respondSticky" && event.payload.origin === "customer") {
        updateTaskListRecord({
          messageId: event.payload.messageId,
          new: true
        });
        addInfoNotification("A customer has replied to you");
      } else if (event.topic.includes(event.payload.messageId) && event.action === NEW_WS_ACTION && event.payload.origin === "expert") {
        updateHistoryMessage(event.payload.messageId);
      }
    }
  };
  const processWebSocketEvent = event => {
    const shouldProcess = message.conversationState ? !isStateLocked(message.conversationState.state, event === null || event === void 0 ? void 0 : event.action) : true;
    logEvent(event, shouldProcess, message);
    switch (event.type) {
      case MESSAGE:
        if (!shouldProcess) {
          return;
        }
        processMessageEvent(event, tasklistContext);
        break;
      case ANNOUNCEMENT:
        event.payload.template = validateTemplateContent(event.payload);
        event.payload.state = "unread";
        addAnnouncement(event.payload);
        if (event.payload.priority === "low") {
          addInfoNotification("You have a new announcement");
        }
        break;
      case RESOURCE_CACHE:
        processMessageResourceCache(event.payload.file);
        break;
      case DIALOGUE:
        if (!shouldProcess) {
          return;
        }
        processDialogueEvent(event, messageContext);
        break;
      case USER:
        if (event.action === MESSAGE_FEE_ALLOCATION_WS_ACTION) {
          updateMessagePendingFeeAllocation(event.payload.mgsPendingFeeAllocation);
        }
        if (event.action === PROFILE_UPDATE_WS_ACTION) {
          updateProfile(event.payload);
        }
        break;
      case RELEASE:
        addInfoNotification("There is a new version of Limitless available, please refresh your browser to get the latest changes", 20000);
        break;
      default:
        processCommonEvent(event);
    }
  };
  const onConnect = client => {
    subscribeToGroups(client, profile);
    commonConnect(client, profile);
    setLocalClient(client);
  };
  const onDisconnect = client => {
    try {
      unSubscribeGroups(client, profile);
      commonDisconnect(client, profile);
      if ((history === null || history === void 0 ? void 0 : history.activeMessages.length) > 0) {
        history.activeMessages.forEach(message => {
          unsubscribeFromSupportChannel(client, message.messageId);
        });
      }
    } catch (error) {
      log.info("Failed to unsubscribe from web sockets %o ", error);
    }
  };

  // Make the context object:
  return {
    onConnect,
    onDisconnect,
    processWebSocketEvent,
    routeName,
    setRouteName,
    onHookInitialisation,
    getWebSocketSubscriptionStatus,
    passiveQrEventFlag,
    setPassiveQrEventFlag,
    passiveRespondEvent,
    setPassiveRespondEvent,
    setPassiveRespondEventData,
    passiveRespondEventData
  };
};